import React, { useEffect, useState, useRef } from 'react';
import { Dialog, DialogContent, IconButton, Box, Typography, Button, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import BankingService from 'services/banking.service';
import { QRCodeSVG } from 'qrcode.react';

interface StaticQRProps {
  open: boolean;
  onClose: () => void;
}

const StaticQR = ({ open, onClose }: StaticQRProps) => {
  const [emvqrcps, setEmvQrcps] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const bankingService = new BankingService();

  useEffect(() => {
    if (open) {
      setLoading(true);
      setEmvQrcps(null);
      setError(false);

      const startGeneration = async () => {
        try {
          const pixKeys = await bankingService.getPixKeys();
          const hasPixKey = pixKeys.pixKeyEvp || pixKeys.pixKeyDoc || pixKeys.pixKeyEmail;

          if (!hasPixKey) {
            await bankingService.requestCreatePixKey({ keyType: 'EVP', key: '' });
          }

          const { id } = await bankingService.createStaticQRCode();

          const checkQRCode = async () => {
            try {
              const qrCode = await bankingService.getQRCode(id);
              if (qrCode.emvqrcps) {
                setEmvQrcps(qrCode.emvqrcps);
                setLoading(false);
                if (pollingRef.current) clearInterval(pollingRef.current);
              }
            } catch {
              // ignore transient polling errors
            }
          };

          await checkQRCode();
          pollingRef.current = setInterval(checkQRCode, 2000);
        } catch {
          setLoading(false);
          setError(true);
        }
      };

      startGeneration();
    }

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 1,
        },
      }}
    >
      <DialogContent sx={{ p: 3 }}>
        <Box position="relative" display="flex" alignItems="center" justifyContent="center" mb={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <QrCode2OutlinedIcon sx={{ color: 'primary.main' }} />
            <Typography fontWeight={600} fontSize={16}>
              Receber PIX QRCode
            </Typography>
          </Box>

          <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', right: 0 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Typography fontSize={13} color="text.secondary" textAlign="center" mb={3}>
          Gere um QRCode para receber um pagamento
        </Typography>

        <Typography fontWeight={600} fontSize={14} textAlign="center" mb={2}>
          QRCode Estático
        </Typography>

        <Box display="flex" justifyContent="center" mb={3} minHeight={200} alignItems="center" flexDirection="column" gap={1}>
          {loading && !emvqrcps && !error && <CircularProgress />}
          {error && (
            <Typography color="error" fontSize={13} textAlign="center">
              Erro ao gerar o QRCode. Feche e tente novamente.
            </Typography>
          )}
          {emvqrcps && <QRCodeSVG value={emvqrcps} size={200} />}
        </Box>

        <Box display="flex" justifyContent="center" gap={2} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<ContentCopyOutlinedIcon />}
            disabled={!emvqrcps}
            sx={{
              borderRadius: '999px',
              px: 3,
              height: 36,
              textTransform: 'none',
              fontWeight: 500,
            }}
            onClick={() => {
              if (emvqrcps) navigator.clipboard.writeText(emvqrcps);
            }}
          >
            Copiar código PIX
          </Button>

          <Button
            variant="outlined"
            startIcon={<ShareOutlinedIcon />}
            disabled={!emvqrcps}
            sx={{
              borderRadius: '999px',
              px: 3,
              height: 36,
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            Compartilhar QRCode
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default StaticQR;
